<template>
  <div class="card">
    <div class="bg-blue-light p-1">
      <TitleButton
          :showBtn="false"
          title="Incentive Payment Voucher List"
          btnTitle="Add New"
          :showAddNew="true"
          @onClickAddNewButton="onClickAddNewButton"
      />
    </div>
    <div class="my-2 px-2">
      <DateQuerySetter
          btnTitle="Go"
          @onClickGo="onClickToGo"
          class="tds-vds-query-setter"
      >
        <div class="col-12 col-sm-6 col-md-4 col-lg-3">
          <label for="colFormLabel" class="col-form-label">Status</label>
          <v-select
              placeholder="Select Status"
              v-model="status"
              :options="statusOptions"
              label="name"
              :reduce="option => option.id"
          />
        </div>
      </DateQuerySetter>
    </div>

    <div class="col-12 px-2 mt-2 mb-4">
      <div class="card p-2" style="min-height: 200px">
        <div class="table-responsive">
          <table class="table">
            <thead>
            <tr>
              <th>Date</th>
              <th>Voucher No</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in tableItems" :key="i">
              <td>
                <span class="fw-bold">{{ item.date }}</span>
              </td>
              <td>{{ item.voucher_no }}</td>
              <td> {{item.note}} </td>
              <td> {{ commaFormat(item.total_amount) }} </td>
              <td>
                {{ startCase(item.status) }}
                <small v-if="item.status === 'cancelled' && item.cancel_note" class="d-block text-danger">
                  Reason: {{ item.cancel_note }}
                </small>
              </td>
              <td>
                <template v-if="item.has_commission_data">
                  <button class="btn btn-primary btn-sm"
                          @click="goToEdit(item.id)"
                          style="cursor: pointer;"
                  >
                    <i class="fas fa-edit"></i>
                  </button>
                  <button class="btn btn-secondary btn-sm"
                          @click="goToPrint(item.id)"
                          style="cursor: pointer; margin-left: 10px"
                  >
                    <i class="fas fa-print"></i>
                  </button>
                </template>
                <button v-if="canCancel(item.status)"
                        class="btn btn-danger btn-sm"
                        @click="openCancelPreview(item)"
                        style="cursor: pointer; margin-left: 10px"
                        title="Cancel Voucher"
                >
                  <i class="fas fa-ban"></i>
                </button>
              </td>
            </tr>

            </tbody>
          </table>
          <p v-if="!tableItems.length" class="text-center mt-5">No data</p>
        </div>
      </div>
    </div>

    <div class="px-2 position-absolute bottom-0 mt-4">
      <Pagination :offset="params.offset" @onPageChange="onPageChange" ref="pagination"/>
    </div>

    <IncentivePaymentCancelModal
        v-if="cancelVoucherData"
        :voucher="cancelVoucherData"
        :loading="isCancelling"
        @close="closeCancelPreview"
        @confirm="onConfirmCancel"
    />

    <Loader v-if="loading"/>

  </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import { inject, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import Loader from '@/components/atom/LoaderComponent'
import Pagination from '@/components/atom/Pagination'
import DateQuerySetter from '@/components/atom/DateQuerySetter.vue';
import { computed } from 'vue';
import figureFormatter from '@/services/utils/figureFormatter'
import handleJournal from '@/services/modules/journal'
import handleSdm from "@/services/modules/sdm/sdm";
import incentivePaymentVoucherPdfHelper from "@/services/utils/incentivePaymentVoucherPdfHelper";
import IncentivePaymentCancelModal from '@/components/molecule/company/sdm/IncentivePaymentCancelModal.vue';
import { startCase } from 'lodash';
import handleChartAccount from '@/services/modules/chartAccount';

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
const tableItems = ref([]);
const paymentRes = ref({});
const params = ref({
  offset: 10,
});
const {commaFormat} = figureFormatter()
const token = computed(() => localStorage.getItem('token'));
const {
  fetchJournals,
} = handleJournal()
const { fetchSingleIncentivePaymentVoucher, cancelIncentivePaymentVoucher } = handleSdm()
const { generatePdf } = incentivePaymentVoucherPdfHelper();
const { fetchHome } = handleChartAccount();
const cancelVoucherData = ref(null);
const isCancelling = ref(false);
const status = ref(null);
const statusOptions = ref([]);

const canCancel = (voucherStatus) => {
  return ['approved', 'pending'].includes(voucherStatus)
}

const getStatusOptions = () => {
  return fetchHome().then(res => {
    if (!res.data || !res.data.approval_status) return
    statusOptions.value = [
      ...Object.entries(res.data.approval_status).map(([id, name]) => ({ id, name })),
      { id: 'cancelled', name: 'Cancelled' }
    ]
  }).catch(() => {
    showError('Something went wrong')
  })
}

const onClickToGo = async () => {
  if (route.query.page && route.query.page !== '1') {
    await router.push({ path: route.path, query: { ...route.query, page: 1 } })
  }
  getIncentiveData();
}

function onClickAddNewButton() {
  router.push({name: `incentive-payment-voucher`, params: route.params, query: route.query})
}

const goToEdit = (id) => {
  router.push({
    name: 'incentive-payment-voucher-edit',
    params: {
      companyId: route.params.companyId,
      moduleId: route.params.moduleId,
      menuId: route.params.menuId,
      pageId: route.params.pageId,
      voucherId: id
    },
    query: route.query
  })
}

const setPaginationData = (data) => {
  store.commit('setPaginationData', data)
};

const setPagination = (data) => {
  setPaginationData({
    records: data.total,
    perPage: data.per_page,
    currentPage: data.current_page,
  })
}

const onPageChange = async (page) => {
  let routeQuery = Object.assign({}, route.query)
  routeQuery.page = page
  await router.push({path: route.path, query: routeQuery})
  await getIncentiveData();
}
const getIncentiveData = async () => {
  const query = getQuery();
  loading.value = true
  tableItems.value = []

  await fetchJournals(query).then(res => {
    if (!res.status){
      store.commit("resetPagination")
      return
    }

    tableItems.value = res.data.data;
    setPagination(res.data)
  }).catch(err => {
    console.log(err)
  }).finally(() => {
    loading.value = false
  })
}

const goToPrint = (id) => {
  let companyId = route.params.companyId;
  const companyQuery = `?company_id=${companyId}`;
  fetchSingleIncentivePaymentVoucher(id, companyQuery).then( (res) => {
    if(res){
      paymentRes.value = res.data;
    }}).then (() => {
    generatePdf(companyId, paymentRes.value)
  }).catch((err) => {
    console.log('Something went wrong');
  })
}

const buildPreviewFromRow = (item) => {
  return {
    data: {
      id: item.id,
      voucher_no: item.voucher_no,
      date: item.date,
      note: item.note,
      total_amount: item.total_amount,
      full_name: item.party ? item.party.full_name : '',
      serial_no: ''
    },
    incentive_payment: [],
    total: {},
    form_data: {}
  }
}

const openCancelPreview = (item) => {
  if (!item.has_commission_data) {
    cancelVoucherData.value = buildPreviewFromRow(item)
    return
  }
  const companyQuery = `?company_id=${route.params.companyId}`;
  loading.value = true

  fetchSingleIncentivePaymentVoucher(item.id, companyQuery).then(res => {
    if (!res.status) return showError(res.message)
    cancelVoucherData.value = res.data
  }).catch(() => {
    showError('Something went wrong')
  }).finally(() => {
    loading.value = false
  })
}

const closeCancelPreview = () => {
  cancelVoucherData.value = null
}

const onConfirmCancel = (note) => {
  const voucherId = cancelVoucherData.value.data.id
  isCancelling.value = true

  cancelIncentivePaymentVoucher(voucherId, {
    company_id: route.params.companyId,
    note: note
  }).then(res => {
    if (!res.status) return showError(res.message)
    showSuccess(res.message)
    closeCancelPreview()
    getIncentiveData()
  }).catch(() => {
    showError('Something went wrong')
  }).finally(() => {
    isCancelling.value = false
  })
}

const getQuery = () => {
  let query = `?company_id=${route.params.companyId}`;
  if (route.query.page) query += '&page=' + route.query.page;
  if (route.query.start) query += '&start_date=' + route.query.start;
  if (route.query.end) query += '&end_date=' + route.query.end;
  query += '&offset=' + params.value.offset;
  if (status.value) query += '&status=' + status.value;
  query += '&voucher_type=payment_voucher&txn_type=incentive_payment'
  return query;
}

onMounted(() => {
  Promise.all([
    getIncentiveData(),
    getStatusOptions(),
  ])
})
</script>
